import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { t, Trans } from 'src/translations'

import Image from 'shared/components/common/Image'
import { useTrackOrderPage } from 'shared/js/hooks/useTrackOrderPage'

import { OrderPageContent } from 'public/components/default_template/OrderPage'
import { OrderPageContentProps } from 'public/components/default_template/online_ordering/pages/OrderPageWrapper'
import { useCateringSetupStatusByRx } from 'public/components/default_template/useCatering'
import { rxLocationSearchUrl } from 'public/components/toast_local/common/searchUtils'

import { resources } from 'config'

import TitleCard from './TitleCard'
import Breadcrumbs, { BreadcrumbItem } from './breadcrumbs/Breadcrumbs'
import CuisinePills from './cuisine_pills/CuisinePills'
import Footer from './footer/Footer'
import { formatRXName } from './formatting'
import HeaderImage from './header_image/HeaderImage'
import Hours from './hours/Hours'
import Location from './location/Location'
import Overview from './overview/Overview'
import {
  RestaurantProfileRestaurantType,
  SimilarRxSearchResultRestaurantType
} from './profileUtils'
import RestaurantDetailsHeader from './restaurant_details_header/RestaurantDetailsHeader'
import RXCarousel from './rx_carousel/RXCarousel'
import { useParams } from 'react-router'
import { OptionalToastLocalSiteData } from 'src/shared/js/types'
import { extractActiveHomePageDomains } from 'src/shared/js/utils/DomainUtils'

type Props = {
  restaurant: RestaurantProfileRestaurantType
  nearbyRxs?: SimilarRxSearchResultRestaurantType[]
  optionalSiteData?: OptionalToastLocalSiteData
  scrollNavRef: React.Ref<HTMLDivElement>
  usingSecondaryNav?: boolean
  showOrderPage?: boolean
  hideRXCarousel?: boolean
  hideFooter?: boolean
  breadcrumbItems?: BreadcrumbItem[]
  useToastAppHeader?: boolean
}

const buildBreadcrumbItems = (
  restaurant: RestaurantProfileRestaurantType
): BreadcrumbItem[] => {
  const items: BreadcrumbItem[] = []

  // Add home link (always first)
  items.push({
    label: t('breadcrumbs.home'),
    href: `https://${resources.toastLocalHost}/local`
  })

  // Add city link if location available
  const locationSearchUrl = rxLocationSearchUrl(restaurant.location)
  if (locationSearchUrl) {
    items.push({
      label: restaurant.location.city,
      href: locationSearchUrl
    })
  }

  // Add restaurant name (will be last/non-clickable)
  items.push({
    label: formatRXName(restaurant.name) || t('breadcrumbs.restaurant'),
    href: '' // Empty href, last item rendered as text automatically
  })

  return items
}

const RestaurantProfile = ({
  restaurant,
  nearbyRxs,
  optionalSiteData,
  scrollNavRef,
  usingSecondaryNav,
  showOrderPage,
  hideRXCarousel,
  hideFooter,
  breadcrumbItems,
  useToastAppHeader = false
}: Props) => {
  const cateringOOLink = useCateringSetupStatusByRx(
    restaurant.guid,
    'toast_local'
  )

  const bannerUrl =
    optionalSiteData?.config?.ooConfig?.heroImage?.src ??
    restaurant.bannerUrls?.medium

  const websiteHref = useMemo(() => {
    if (!optionalSiteData) return null
    const { config } = optionalSiteData

    const hasActiveWebsite =
      config?.hasFullCustomization !== false &&
      !config?.isOnlineOrderingOnly &&
      config?.previewMode !== true
    if (!hasActiveWebsite) return null
    return extractActiveHomePageDomains(optionalSiteData)?.at(0) ?? null
  }, [optionalSiteData])

  const breadcrumbs = breadcrumbItems ?? buildBreadcrumbItems(restaurant)
  const shouldShowBreadcrumbs = breadcrumbs.length > 0

  return (
    <>
      <div className='paddedSection restaurantProfile'>
        {shouldShowBreadcrumbs && <Breadcrumbs items={breadcrumbs} />}
        {useToastAppHeader ? (
          <RestaurantDetailsHeader
            id='rx_profile'
            rxName={formatRXName(restaurant.name || '')}
            location={restaurant.location}
            bannerImageUrl={bannerUrl}
            cuisineType={restaurant.cuisineType ?? undefined}
            schedule={restaurant.schedule}
            timeZoneId={restaurant.timeZoneId ?? undefined}
            phone={restaurant.location?.phone}
            giftCardHref={restaurant.giftCardLinks?.purchaseLink}
            websiteHref={websiteHref}
            restaurantShortUrl={restaurant.shortUrl ?? undefined}
            hasReservations={
              restaurant.bookingConfig?.onlineReservationsEnabled ||
              restaurant.bookingConfig?.waitlistEnabled ||
              false
            }
          />
        ) : (
          <div className='lg-reverse'>
            <HeaderImage
              id='rx_profile'
              rxName={formatRXName(restaurant.name || '')}
              location={restaurant.location}
              bannerImageUrl={bannerUrl}
            />
            <div className='rx-info'>
              <div>
                <CuisinePills restaurant={restaurant} />
                <div className='title-info'>
                  <h1 className='rx-page-section'>
                    {formatRXName(restaurant.name || '')}
                  </h1>
                </div>
              </div>
              <div className='overview-container'>
                <Overview
                  hoursHref='#rx-hours'
                  phone={restaurant.location?.phone}
                  location={restaurant.location}
                  giftCardHref={restaurant.giftCardLinks?.purchaseLink}
                  cateringOOHref={cateringOOLink}
                  websiteHref={websiteHref}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {showOrderPage ? (
        <RestaurantProfileOrderContent
          scrollNavRef={scrollNavRef}
          hideHero={true}
          usingSecondaryNav={usingSecondaryNav}
          hideToastLocalFulfillmentPanel={useToastAppHeader}
        />
      ) : restaurant.hasOnlineOrderingModule ||
        restaurant.hasToastLocalModule ? (
        <div className='paddedSection'>
          <div className='rx-page-section'>
            <div className='order-link'>
              <Image alt='Takeout icon' src='toastLocal/icons/pick-up.svg' />
              <div>
                <Trans
                  i18nKey='restaurant-profile.looking-for-pickup-delivery'
                  components={{
                    Link: (
                      <Link
                        to={`/local/order${
                          restaurant.shortUrl ? `/${restaurant.shortUrl}` : ''
                        }`}
                        className='link-to-rx-profile'
                      />
                    )
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className='paddedSection rxPageSectionWrapper'>
        <div>
          <hr className='divider' />
        </div>
        <div className='rx-page-section rx-cards'>
          <div className='rx-hours' id='hours'>
            <TitleCard
              title={t('restaurant-profile.all-hours')}
              defaultState='open'
            >
              <Hours schedule={restaurant.schedule} />
            </TitleCard>
          </div>
          {(restaurant.description ||
            restaurant.socialMediaLinks?.restaurantLink) && (
            <div className='rx-description'>
              <TitleCard
                title={t('restaurant-profile.restaurant-info')}
                defaultState='open'
              >
                {restaurant.description && <p>{restaurant.description}</p>}
                {restaurant.socialMediaLinks?.restaurantLink && (
                  <p>
                    <a
                      href={restaurant.socialMediaLinks?.restaurantLink}
                      target='_blank'
                      rel='noreferrer noopener'
                    >
                      {t('restaurant-profile.website')}
                    </a>
                  </p>
                )}
              </TitleCard>
            </div>
          )}
          {restaurant.location && (
            <div className='location'>
              <TitleCard
                title={t('restaurant-profile.location')}
                defaultState='open'
              >
                <Location
                  restaurantGuid={restaurant.guid}
                  location={restaurant.location}
                  restaurantName={formatRXName(restaurant.name || '')}
                />
              </TitleCard>
            </div>
          )}
        </div>
      </div>
      {!hideRXCarousel && nearbyRxs?.length ? (
        <div className='paddedSection rxPageSectionWrapper'>
          <div>
            <hr className='divider' />
          </div>
          <RXCarousel
            title={t('restaurant-profile.similar-restaurants-in-area')}
            elements={nearbyRxs}
            className='rx-page-section'
            elementClass='pure-u-12-24 pure-u-md-8-24 pure-u-lg-6-24'
            moreRxHref={rxLocationSearchUrl(restaurant.location)}
            moreRxDescription={t(
              'restaurant-profile.search-similar-restaurants'
            )}
          />
        </div>
      ) : null}
      {!hideFooter && <Footer />}
    </>
  )
}

export default RestaurantProfile

const RestaurantProfileOrderContent = (props: OrderPageContentProps) => {
  const params = useParams<{ itemSlug?: string; guid?: string }>()
  const itemGuid = params.itemSlug?.split('_')?.[1] ?? params.guid
  useTrackOrderPage(false)

  return (
    <div className='rx-order-page'>
      <OrderPageContent {...props} itemGuid={itemGuid} />
    </div>
  )
}
